import { getRandomInt } from 'hearts-engine';
import { GameObjects } from 'phaser';
import { getResolution } from '@/GameUI/Constants';
const { Container, Graphics } = GameObjects;

const TEXT_STYLE = {
	fontFamily: 'Arial',
	fontSize: 26,
	fill: '#000000',
	align: 'center',
	resolution: getResolution(),
};

const PADDING = 20;
const HALF_PADDING = PADDING / 2;
export const MINI_SCORE_WIDTH = 200;

const LINE_HEIGHT = 45;
export const MINI_SCORE_HEIGHT = LINE_HEIGHT * 2 - 10;
const HALF_HEIGHT = MINI_SCORE_HEIGHT / 2;

const SCORE_AREA_Y = -LINE_HEIGHT / 2 + HALF_PADDING / 2;
const NAME_Y = 20;

const BACKGROUND_COLOR = 0x479ACE;

function getRandomAvatarURL() {
	return `AvatarVV_${getRandomInt(1, 233).toString().padStart(4, '0')}.png`;
}

class ScoreArea extends Container {
	private heartsCount: GameObjects.Text;
	private queenCount: GameObjects.Text;
	private heartsIcon: GameObjects.Image;
	private queenIcon: GameObjects.Image;
	private avatar: GameObjects.Image;
	private currentAvatar: string;

	constructor(scene: Phaser.Scene, avatar: string) {
		super(scene);

		this.avatar = new GameObjects.Image(scene, -100, 18, 'Avatar', avatar);
		this.avatar.displayHeight = this.avatar.displayWidth = (LINE_HEIGHT*2) - 10;
		//this.avatar.setOrigin(0, .5);
		this.currentAvatar = avatar;

		this.heartsCount = new GameObjects.Text(scene, -20, 0, '13', TEXT_STYLE);
		this.heartsCount.setOrigin(1, 0.5);

		this.heartsIcon = new GameObjects.Image(scene, -18, 0, 'miniHeart');
		this.heartsIcon.setOrigin(0, 0.5);

		this.queenCount = new GameObjects.Text(scene, 50, 0, '13', TEXT_STYLE);
		this.queenCount.setOrigin(1, 0.5);

		this.queenIcon = new GameObjects.Image(scene, 50, 0, 'miniSpade');
		this.queenIcon.setOrigin(0, 0.5);

		this.add(this.avatar);
		this.add(this.heartsCount);
		this.add(this.heartsIcon);
		this.add(this.queenCount);
		this.add(this.queenIcon);
	}

	setHearts(heartsCount: number) {
		this.heartsCount.setText(heartsCount.toString());
	}

	setHasQueen(hasQueen: boolean) {
		this.queenCount.setText(hasQueen ? '13' : '0');
	}

	async setAvatar(avatar: string) {
		if (!avatar) {
			return;
		}

		if (avatar === this.currentAvatar) {
			return; //no need to update, already have it
		}

		this.currentAvatar = avatar;

		const avatarTextures = this.scene.textures.get('Avatar');

		if (avatarTextures.has(avatar)) { //easiest case, already have it by name
			this.avatar.setTexture('Avatar', avatar);
			return;
		}

		if (avatar.indexOf('.png') < 0) { //Maybe we should try the texture atlas and add ".png" since that's what the atlas has
			const withExtension = `${avatar}.png`;

			if (avatarTextures.has(withExtension)) { //easiest case, already have it by name
				this.avatar.setTexture('Avatar', withExtension);
				return;
			}
		}

		//If we've gotten to this point, we're treating as a remote resource
		const key = `remoteRSC${avatar}`;

		if (this.scene.textures.exists(key)) { //Have we already fetched it?
			this.avatar.setTexture(key);
			return;
		}

		//Fetch remote resource and put it into the texture manager
		await new Promise<void>((resolve, reject) => {
			this.scene.load.image(key, avatar);
			this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
				resolve();
			});
			this.scene.load.once(Phaser.Loader.Events.FILE_LOAD_ERROR, () => {
				reject();
			});
			this.scene.load.start();
		});

		this.avatar.setTexture(key);
	}
}

export default class MiniScore extends Container {
	private namePlate: GameObjects.Text;
	private background: GameObjects.Graphics;
	private scoreArea: ScoreArea;
	private currentName: string;

	constructor(scene: Phaser.Scene, name = '', avatar = getRandomAvatarURL()) {
		super(scene);

		this.scoreArea = new ScoreArea(scene, avatar);

		this.namePlate = new GameObjects.Text(scene, 0, NAME_Y, name, TEXT_STYLE);
		this.namePlate.setOrigin(.5, .5);
		this.currentName = name;

		//background
		this.background = new Graphics(scene);

		this.add(this.background);
		this.add(this.namePlate);
		this.scoreArea.x = 0;
		this.scoreArea.y = SCORE_AREA_Y;

		this.add(this.scoreArea);
		this.updateBackground();
	}

	setHearts(heartsCount: number) {
		this.scoreArea.setHearts(heartsCount);
	}

	setHasQueen(hasQueen: boolean) {
		this.scoreArea.setHasQueen(hasQueen);
	}

	setPlayerName(newName: string) {
		if (this.currentName === newName) {
			return; //no need to update
		}

		this.currentName = newName;
		this.namePlate.setText(newName);
		this.updateBackground();
	}

	setAvatar(avatar: string) {
		return this.scoreArea.setAvatar(avatar);
	}

	private updateBackground() {
		const size = Phaser.GameObjects.GetTextSize(this.namePlate, this.namePlate.getTextMetrics(), [this.namePlate.text]);
		this.width = Math.max(MINI_SCORE_WIDTH, size.width + PADDING);
		const halfWidth = this.width / 2;
		this.background.clear();
		this.background.fillStyle(BACKGROUND_COLOR);
		this.background.lineStyle(0, 0xFFFFFF, 1);
		this.background.fillRoundedRect(-halfWidth, -HALF_HEIGHT, this.width, MINI_SCORE_HEIGHT, 10);
	}
}
